import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  AdminApiPaths,
  TApiResponse,
  APIDataProvider,
  TApiPaginationResponse,
} from '../../../api';
import {
  IFlexiblePlan,
  IFlexiblePlanDeleteVerificationData,
  TFlexiblePlanStatuses,
} from '.';

export type TAdminCreateFlexiblePlan = {
  name: IFlexiblePlan['name'];
  numberOfCredits?: IFlexiblePlan['numberOfCredits'];
  expiredIn: IFlexiblePlan['expiredIn'];
  status: IFlexiblePlan['status'];
  features: IFlexiblePlan['features'];
  isMostPopular: boolean;
};

export type TAdminGetFlexiblePlans = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  searchText?: string;
  statuses?: TFlexiblePlanStatuses[];
};

export type TAdminUpdateFlexiblePlan = {
  id: string;
  name: IFlexiblePlan['name'];
  numberOfCredits?: IFlexiblePlan['numberOfCredits'];
  expiredIn: IFlexiblePlan['expiredIn'];
  status: IFlexiblePlan['status'];
  features: IFlexiblePlan['features'];
  isMostPopular: boolean;
};

export type TAdminGetUserFlexiblePlans = {
  userId: string;
};

const apiDataProvider = new APIDataProvider();

const adminGetFlexiblePlansModule = async ({
  limit,
  offset,
  sortKey,
  sortValue,
  statuses,
  searchText,
}: TAdminGetFlexiblePlans) => {
  let path = `${AdminApiPaths.adminFlexiblePlans}?limit=${limit}&offset=${offset}`;

  if (sortKey) {
    path += `&sortKey=${sortKey}`;
  }

  if (sortKey) {
    path += `&sortValue=${sortValue}`;
  }

  if (searchText) {
    path += `&searchText=${searchText}`;
  }

  if (statuses) {
    path += `&statuses=${statuses}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<IFlexiblePlan>>
  >(path);

  return response.data;
};

export const adminCreateFlexiblePlan = createAsyncThunk(
  '/admin/flexible-plans/create',
  async (data: TAdminCreateFlexiblePlan) => {
    const path = `${AdminApiPaths.adminFlexiblePlans}/`;

    const response = await apiDataProvider.post<TApiResponse<IFlexiblePlan>>(
      path,
      data
    );

    return response.data;
  }
);

export const adminUpdateFlexiblePlan = createAsyncThunk(
  '/admin/flexible-plans/update',
  async (data: TAdminUpdateFlexiblePlan) => {
    const path = `${AdminApiPaths.adminFlexiblePlans}/${data.id}`;

    const response = await apiDataProvider.put<TApiResponse<IFlexiblePlan>>(
      path,
      data
    );

    return response.data;
  }
);

export const adminDeleteFlexiblePlan = createAsyncThunk(
  '/admin/flexible-plans/delete',
  async (id: string) => {
    const path = `${AdminApiPaths.adminFlexiblePlans}/${id}`;

    const response = await apiDataProvider.delete<TApiResponse<IFlexiblePlan>>(
      path
    );

    return response.data;
  }
);

export const adminVerifyFlexiblePlanDelete = createAsyncThunk(
  '/admin/flexible-plans/verify-delete',
  async (id: string) => {
    const path = `${AdminApiPaths.adminFlexiblePlans}/verify-delete/${id}`;

    const response = await apiDataProvider.get<
      TApiResponse<IFlexiblePlanDeleteVerificationData>
    >(path);

    return response.data;
  }
);

export const adminGetUserFlexiblePlans = createAsyncThunk(
  '/admin/flexible-plans/user/list',
  async ({ userId }: TAdminGetUserFlexiblePlans) => {
    const path = `${AdminApiPaths.adminFlexiblePlans}/user/${userId}?userId=${userId}`;

    const response = await apiDataProvider.get<TApiResponse<IFlexiblePlan[]>>(
      path
    );

    return response.data;
  }
);

export const adminGetFlexiblePlans = createAsyncThunk(
  '/admin/flexible-plans/list',
  adminGetFlexiblePlansModule
);

export const adminGetFlexiblePlansForDropDown = createAsyncThunk(
  '/admin/flexible-plans/dropdown-list',
  adminGetFlexiblePlansModule
);
